﻿import React from 'react';

import { ArcGauge, CircularGauge, LinearGauge, RadialGauge } from '@progress/kendo-react-gauges';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ReportComponent } from 'apis/ReportApi';
import useResizeObserver from 'use-resize-observer';
import { Col, Container } from 'react-bootstrap';

import 'components/GeneralGaugeChart/general-gauge.scss';

export interface ColorSection {
    color: string; // hex color
    from?: number; // starting number of section
    to?: number; // ending number of section, don't need to declare 0
}

interface GeneralGaugeProps {
    component: ReportComponent;
    index: number;
    isSubReport?: boolean;
    loading: boolean;
}

export function GeneralGauge({ component, index, isSubReport = false, loading }: GeneralGaugeProps) {

    const { formatMessage } = useIntl();
    const { height, ref, width } = useResizeObserver<HTMLDivElement>();

    const arcCenterRenderer = (value: number, color: string) => (
        <>
            <h4 style={{ color: color }}>
                {component.Results?.Data[0].Title}
            </h4>
            <h2>
                {component.Results?.Data[0].Value ?? value}
            </h2>
        </>
    );

    const centerRenderer = (value: number, color: string) => (
        <>
            <h2>
                {value}
            </h2>
        </>
    );

    const getGauge = (width: number | undefined) => {

        switch (component.Type) {

            case 'Arc':
                return (
                    <div className='arcGauge-wrapper'>
                        <ArcGauge
                            scale={{
                                endAngle: 200,
                                labels: { visible: true },
                                max: component.Results?.Data[0].MaxValue,
                                startAngle: -20
                            }}
                            arcCenterRender={arcCenterRenderer}
                            className={` ${width && width < 230 && 'guage-custom'} w-100`}
                            colors={[{ color: component.Results?.Data[0].Color }]}
                            value={component.Results?.Data[0].Value}
                        />
                    </div>
                );
            case 'Circular':
                return (
                    <CircularGauge
                        centerRender={centerRenderer}
                        colors={component.Results?.Data[0].Color}
                        value={component.Results?.Data[0].Value}
                    />
                );

            case 'Linear':
                return <LinearGauge pointer={{ value: component.Results?.Data[0].Value }} />;

            case 'Radial':
                return <RadialGauge pointer={{ value: component.Results?.Data[0].Value }} />;

            default:
                return formatMessage({ id: 'error_type_for_gauge_is_not_an_actual_ty.d141963f', defaultMessage: 'Error: Type for gauge is not an actual type' });

        }

    };

    const getImprovementIcon = () => {

        const improvementValue = component.Results?.Data[0].Value - component.Results?.Data[0].PreviousValue;
        if (improvementValue === 0) {

            return <i className="fa-solid fa-dash" style={{ color: '#a7a9ab', lineHeight: 2 }}></i>;

        } else if (improvementValue > 0) {

            return <i className="fa-solid fa-triangle" style={{ color: '#0aba94', lineHeight: 2 }}></i>;

        } else if (improvementValue < 0) {

            return (
                <i
                    className="fa-solid fa-triangle fa-rotate-180"
                    style={{ color: '#e44a4c', lineHeight: 2, paddingTop: '10px' }}
                ></i>
            );

        }

    };

    return (
        <>
            {loading ? (
                <GMetrixLoader/>
            ) : (
                <Col
                    className="component-wrapper__col print-only-column"
                    id={index.toString()}
                    key={`${component.ComponentId}-${index}`}
                    lg={isSubReport ? 12 : component.WidthLg}
                    md={isSubReport ? 12 : component.WidthMd}
                    ref={ref}
                    sm={isSubReport ? 12 : component.WidthSm}
                    xl={isSubReport ? 12 : component.WidthXl}
                    xxl={component.WidthXl}
                >
                    <Container className="chart-generic text-center">
                        <div className="d-flex flex-row justify-content-between">
                            {component.Title && (
                            <h6 className="text-start chart-generic__title">
                                {component.Title}
                            </h6>
                            )}
                            {component.Results?.Data[0].PreviousValue ? (
                                <div className="text-end">
                                    <div className="d-flex flex-row">
                                        {/* true needs to be set by a function once the numbers are given from DB */}
                                        {getImprovementIcon()}
                                        <h6 className="text-start chart-generic__title ms-2">
                                            {Number(
                                                component.Results?.Data[0].Value -
                                                component.Results?.Data[0].PreviousValue
                                            ).toFixed(2)}
                                        </h6>
                                    </div>
                                    <small>
                                        <FormattedMessage defaultMessage="From Last Attempt" id="from_last_attempt.41179e12" />
                                    </small>
                                </div>
                            ) : (
                                null // I can't figure out why some random 0 appears so I'm just going to do this
                            )}
                        </div>
                        {getGauge(width)}
                    </Container>
                </Col>
            )}
        </>
    );

}
